import React from "react"
import { CartItem } from "@redux/types"
import { getDecodedShopifyProductIdString } from "@utils/index"
import { getProductURL } from "@utils/urls"
import _ from "lodash-es"

const KLAVIYO_ACTIVE_ON_SITE = "Active on Site"
const KLAVIYO_VIEWED_COLLECTION = "Viewed Collection"
const KLAVIYO_VIEWED_PRODUCT = "Viewed Product"
const KLAVIYO_ADDED_TO_CART = "Added to Cart"
const KLAVIYO_STARTED_CHECKOUT = "Started Checkout"

export interface KlaviyoCollectionProps {
  collection: string
  products: Array<KlaviyoProductProps>
}

export interface KlaviyoProductProps {
  sku: string
  shopifyProductId: string // Shopify Product ID
  productSlug: string
  productName: string
  variantName: string
  image?: string
  price: string
  compareAtPrice?: string
  brand: string
  device: string
  deviceType: string
  quantity?: string | number
}

const formatKlaviyoProduct = ({
  sku,
  shopifyProductId,
  productSlug,
  productName,
  variantName,
  image,
  price,
  compareAtPrice,
  brand,
  device,
  deviceType,
  quantity,
}: KlaviyoProductProps) => {
  const product: any = {
    ProductID: getDecodedShopifyProductIdString(shopifyProductId),
    ProductName: productName,
    SKU: sku,
    Brand: brand,
    Categories: [`${deviceType} / ${device}`],
    Price: price,
    URL: getProductURL(productSlug),
  }

  if (image) {
    product.ImageURL = image
  }

  if (compareAtPrice) {
    product.CompareAtPrice = compareAtPrice
  }

  if (variantName) {
    product.Variant = variantName
  }

  if (quantity !== undefined) {
    product.quantity = quantity === "string" ? quantity : quantity.toString()
  }

  return product
}

const getKlaviyo = () => {
  const _window = typeof window !== "undefined" && (window as any)
  if (_window && _window.klaviyo) {
    return {
      identify: _window.klaviyo.identify || function () {},
      push: _window.klaviyo.push || function () {},
      track: _window.klaviyo.track || function () {},
    }
  }
}

export default () => {
  const klaviyo: any = getKlaviyo()

  const klaviyoOnUserData = React.useCallback(
    (user: any) => {
      if (klaviyo && user.id) {
        klaviyo.push([
          "identify",
          {
            $email: user.email,
          },
        ])
        klaviyo.push([
          "track",
          KLAVIYO_ACTIVE_ON_SITE,
          {
            $email: user.email,
          },
        ])
      }
    },
    [klaviyo]
  )

  const klaviyoOnLogin = React.useCallback(
    (user: any) => {
      if (klaviyo) {
        klaviyo.push([
          "identify",
          {
            $email: user.email,
          },
        ])
        klaviyo.push([
          "track",
          KLAVIYO_ACTIVE_ON_SITE,
          {
            $email: user.email,
          },
        ])
      }
    },
    [klaviyo]
  )

  const klaviyoOnViewCollection = React.useCallback(
    ({ collection, products }: KlaviyoCollectionProps) => {
      if (klaviyo) {
        const items = products.map(formatKlaviyoProduct)
        const itemNames = items.map(({ ProductName }) => ProductName)

        klaviyo.push([
          "track",
          KLAVIYO_VIEWED_COLLECTION,
          {
            CollectionName: collection,
            ItemNames: itemNames,
            Items: items,
          },
        ])
      }
    },
    [klaviyo]
  )

  // Trigger only once
  const klaviyoOnViewItem = React.useCallback(
    (product: KlaviyoProductProps) => {
      if (klaviyo) {
        klaviyo.push([
          "track",
          KLAVIYO_VIEWED_PRODUCT,
          formatKlaviyoProduct(product),
        ])
      }
    },
    [klaviyo]
  )

  const klaviyoOnAddToCart = React.useCallback(
    (
      product: KlaviyoProductProps,
      cartProducts: Array<CartItem>,
      cartSubtotal: string,
      checkoutUrl: string
    ) => {
      if (klaviyo) {
        const addedItem = formatKlaviyoProduct(product)
        const items = cartProducts.map(formatKlaviyoProduct)
        const itemNames = items.map(({ ProductName }) => ProductName)

        klaviyo.push([
          "track",
          KLAVIYO_ADDED_TO_CART,
          {
            $value: parseFloat(cartSubtotal),
            AddedItemProductName: addedItem.ProductName,
            AddedItemProductID: addedItem.ProductId,
            AddedItemSKU: addedItem.SKU,
            AddedItemCategories: addedItem.Categories,
            AddedItemImageURL: addedItem.ImageURL,
            AddedItemURL: addedItem.URL,
            AddedItemPrice: addedItem.Price,
            AddedItemQuantity: 1,
            ItemNames: itemNames,
            CheckoutURL: checkoutUrl,
            Items: items,
          },
        ])
      }
    },
    [klaviyo]
  )

  const klaviyoOnStartCheckout = React.useCallback(
    (
      cartProducts: Array<CartItem>,
      cartSubtotal: string,
      checkoutUrl: string
    ) => {
      if (klaviyo) {
        const items = cartProducts.map(formatKlaviyoProduct)
        const itemNames = items.map(({ ProductName }) => ProductName)
        const itemCategories = items.map(
          ({ ProductCategories }) => ProductCategories
        )

        klaviyo.push([
          "track",
          KLAVIYO_STARTED_CHECKOUT,
          {
            $value: parseFloat(cartSubtotal),
            ItemNames: itemNames,
            Categories: itemCategories,
            CheckoutURL: checkoutUrl,
            Items: items,
          },
        ])
      }
    },
    [klaviyo]
  )

  return {
    klaviyoOnUserData,
    klaviyoOnLogin,
    klaviyoOnViewCollection,
    klaviyoOnViewItem,
    klaviyoOnAddToCart,
    klaviyoOnStartCheckout,
  }
}
